import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

// Define an interface for better type safety
interface UserViewModel {
  name: string;
  checked: boolean;
}

@Component({
  selector: "app-select-repo-user",
  templateUrl: "./select-repo-user.component.html", // Ensure this uses filteredUsersData
  styleUrl: "./select-repo-user.component.scss",
})
export class SelectRepoUserComponent implements OnInit {
  // --- State Properties ---
  usersData: UserViewModel[] = []; // Original full list of users
  filteredUsersData: UserViewModel[] = []; // List displayed in the template (after filtering)
  selectedUserNames = new Set<string>(); // Efficiently stores selected user names
  searchTerm: string = ""; // Bound to the search input
  modalTitle: string = "Create Gitea User(s) Account"; // Title for the modal dialog
  // --- Select All Checkbox State ---
  isAllFilteredSelected: boolean = false;
  isIndeterminate: boolean = false;
  confirmButton: string = "Create Account"; // Default confirm button text
  cancelButton: string = "Cancel"; // Default cancel button text

  constructor(
    public dialog: MatDialogRef<SelectRepoUserComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      UserList: string[]; // Expecting an array of user names
      // Optional: Pass in already selected users if needed
      // previouslySelected?: string[];
      title?: string; // Optional title for the modal
      cancelButtonText?: string;
      confirmButtonText?: string;
    }
  ) {}

  ngOnInit(): void {
    if (this.data.title) {
      this.modalTitle = this.data.title; // Use the provided title if available
    }
    if (this.data.cancelButtonText) {
      this.cancelButton = this.data.cancelButtonText; // Use the provided cancel button text if available
    }
    if (this.data.confirmButtonText) {
      this.confirmButton = this.data.confirmButtonText; // Use the provided confirm button text if available
    }
    // Initialize usersData from the input list
    this.usersData = this.data.UserList.map((user): UserViewModel => {
      // Check if this user was previously selected (if that data is passed)
      // const isChecked = this.data.previouslySelected?.includes(user) ?? false;
      // if (isChecked) {
      //     this.selectedUserNames.add(user);
      // }
      return {
        name: user,
        checked: false, // Start all as unchecked (or use isChecked logic above)
      };
    });

    // Initially, the filtered list is the complete list
    this.filterUsers(); // Call filterUsers to initialize filteredUsersData and checkbox state
  }

  /**
   * Filters the usersData based on the searchTerm and updates filteredUsersData.
   */
  filterUsers(): void {
    if (!this.searchTerm) {
      this.filteredUsersData = [...this.usersData];
    } else {
      const lowerCaseSearchTerm = this.searchTerm.toLowerCase();
      this.filteredUsersData = this.usersData.filter((user) =>
        user.name.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    // After filtering, update the state of the "Select All" checkbox
    this.updateSelectAllCheckboxState();
  }

  /**
   * Handles the change event of an individual user checkbox.
   * @param user The user object whose selection changed.
   * @param isChecked The new checked state.
   */
  toggleUserSelection(user: UserViewModel, isChecked: boolean): void {
    // 1. Update the 'checked' state in the filtered list (important for immediate UI reflection)
    user.checked = isChecked;

    // 2. Find the corresponding user in the original list and update its state
    const originalUser = this.usersData.find((u) => u.name === user.name);
    if (originalUser) {
      originalUser.checked = isChecked;
    }

    // 3. Update the Set of selected names
    if (isChecked) {
      this.selectedUserNames.add(user.name);
    } else {
      this.selectedUserNames.delete(user.name);
    }

    // 4. Update the state of the "Select All" checkbox
    this.updateSelectAllCheckboxState();
  }

  /**
   * Handles the change event of the "Select All" checkbox for the filtered list.
   * @param isChecked The new checked state of the "Select All" checkbox.
   */
  setAllFiltered(isChecked: boolean): void {
    this.isAllFilteredSelected = isChecked; // Set the state directly

    // Update all users currently visible in the filtered list
    this.filteredUsersData.forEach((user) => {
      // Only change if the state is different to avoid unnecessary updates
      if (user.checked !== isChecked) {
        user.checked = isChecked; // Update filtered list item

        // Also update the original list item
        const originalUser = this.usersData.find((u) => u.name === user.name);
        if (originalUser) {
          originalUser.checked = isChecked;
        }
      }

      // Update the master Set of selected names
      if (isChecked) {
        this.selectedUserNames.add(user.name);
      } else {
        // Only remove if it exists in the filtered list
        this.selectedUserNames.delete(user.name);
      }
    });

    // Update the indeterminate state (should be false after selecting/deselecting all)
    this.isIndeterminate = false;
  }

  /**
   * Updates the isAllFilteredSelected and isIndeterminate flags
   * based on the selection state of the items in filteredUsersData.
   */
  private updateSelectAllCheckboxState(): void {
    if (this.filteredUsersData.length === 0) {
      this.isAllFilteredSelected = false;
      this.isIndeterminate = false;
      return;
    }

    const numSelected = this.filteredUsersData.filter(
      (user) => user.checked
    ).length;
    const numTotal = this.filteredUsersData.length;

    this.isAllFilteredSelected = numSelected === numTotal;
    this.isIndeterminate = numSelected > 0 && numSelected < numTotal;
  }

  /**
   * Closes the dialog without returning data.
   */
  onClose(): void {
    this.dialog.close({ status: false, data: null });
  }

  /**
   * Closes the dialog and returns the list of selected user names.
   */
  onSubmit(): void {
    // Convert the Set back to an array for the output
    const selectedData = Array.from(this.selectedUserNames);
    this.dialog.close({ status: true, data: selectedData });
  }
}
